import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  ComponentType,
} from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import { Settings } from '@atlaskit/atlassian-navigation';
import CreditCardIcon from '@atlaskit/icon/core/migration/credit-card--creditcard-filled';
import LinkExternalIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import PeopleGroupIcon from '@atlaskit/icon/core/migration/people-group';
import ActivityIcon from '@atlaskit/icon/glyph/activity';
import AtlassianIcon from '@atlaskit/icon/glyph/emoji/atlassian';
import LabsIcon from '@atlaskit/icon/glyph/jira/labs';
import PersonCircleIcon from '@atlaskit/icon/glyph/person-circle';
import { LinkItem, PopupMenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';

import { Workspace } from 'src/components/types';

import { MenuItem } from '../../types';
import urls from '../../urls';

import {
  HorizontalNavContext,
  HorizontalNavProps,
} from './horizontal-nav-context';
import messages from './horizontal-nav.i18n';
import { PopupMenuHeader, BlueBg, DarkBg } from './horizontal-settings.style';

enum MenuItemIds {
  WorkspaceSettings = 'workspace-settings-horizontal-link',
}

type SettingsType = 'workspace' | 'aid-account' | 'bbc-account' | 'labs';

type HorizontalSettingsBaseProps = {
  currentWorkspace?: Workspace;
  isOrgAdmin?: boolean;
  workspaceOrgId?: string;
  ccpEntitlementId?: string;
  ccpTransactionAccountId?: string;
  canViewEntitlement?: boolean;
  manageAccountUrl?: string;
  userEmail?: string | null;
  adminHubUrl?: string;
  loginUrl?: string;
  menuItems: any;
  publishUiEvent?: (event: any) => void;
  triggerComponent: ComponentType<any>;
};

type HorizontalSettingsBaseWithIntlProps = HorizontalSettingsBaseProps & {
  intl: InjectedIntl;
};

// Shared base component used by both nav v3 and v4 systems.
// Nav v3: gets data from HorizontalNavContext, uses @atlaskit/atlassian-navigation trigger
// Nav v4: gets data from useTopNavData hook, uses @atlaskit/navigation-system trigger
export const HorizontalSettingsBase = injectIntl(
  ({
    intl, // from injectIntl
    currentWorkspace,
    isOrgAdmin,
    workspaceOrgId,
    ccpEntitlementId,
    ccpTransactionAccountId,
    canViewEntitlement,
    manageAccountUrl,
    userEmail,
    adminHubUrl,
    loginUrl,
    menuItems,
    publishUiEvent,
    triggerComponent,
  }: HorizontalSettingsBaseWithIntlProps): JSX.Element => {
    const { formatMessage } = intl;

    const workspaceUuid = currentWorkspace?.uuid;

    const hasBillingAccess = useMemo(
      () => canViewEntitlement && ccpEntitlementId && ccpTransactionAccountId,
      [canViewEntitlement, ccpEntitlementId, ccpTransactionAccountId]
    );

    const workspaceSettingsItem = menuItems.settingsItems?.find(
      (item: MenuItem) => item.id === MenuItemIds.WorkspaceSettings
    );

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { push } = useRouterActions();

    const onToggle = useCallback(() => {
      setIsOpen(!isOpen);

      publishUiEvent?.({
        action: 'clicked',
        actionSubject: 'navigationItem',
        actionSubjectId: 'settings',
        source: 'navigation',
      });
    }, [setIsOpen, isOpen, publishUiEvent]);
    const fireEvent = useCallback(
      (settingsType: SettingsType) => {
        publishUiEvent?.({
          action: 'clicked',
          actionSubject: 'link',
          actionSubjectId: 'globalNavSettingsMenu',
          source: 'navigation',
          attributes: {
            settingsType,
          },
        });
      },
      [publishUiEvent]
    );

    const onClose = useCallback(() => {
      setIsOpen(false);
    }, [setIsOpen]);

    const handleClick = useCallback(
      (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        settingsType: SettingsType,
        href: string
      ) => {
        fireEvent(settingsType);
        if (!(e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          push(href);
          setIsOpen(false);
        }
      },
      [fireEvent, push]
    );

    const SettingsMenu = useMemo(() => {
      return () => (
        <PopupMenuGroup data-testid="settings-popup-content" maxWidth={500}>
          <PopupMenuHeader>{formatMessage(messages.settings)}</PopupMenuHeader>
          {workspaceUuid &&
            workspaceOrgId &&
            adminHubUrl &&
            (isOrgAdmin || hasBillingAccess) && (
              <Section title={formatMessage(messages.atlassianAdmin)}>
                {isOrgAdmin && (
                  <LinkItem
                    rel="noopener noreferrer"
                    iconBefore={
                      <DarkBg>
                        <PeopleGroupIcon
                          label=""
                          size="small"
                          LEGACY_size="small"
                          color={token('color.icon.inverse')}
                          LEGACY_primaryColor={token('color.icon.inverse')}
                        />
                      </DarkBg>
                    }
                    iconAfter={
                      <LinkExternalIcon
                        label=""
                        LEGACY_size="small"
                        size="small"
                      />
                    }
                    description={formatMessage(messages.userManagementDesc)}
                    href={urls.ui.adminHub(
                      adminHubUrl,
                      workspaceOrgId,
                      workspaceUuid,
                      'users'
                    )}
                    target="_blank"
                  >
                    {formatMessage(messages.userManagementTitle)}
                  </LinkItem>
                )}
                {hasBillingAccess && (
                  <LinkItem
                    rel="noopener noreferrer"
                    iconBefore={
                      <DarkBg>
                        <CreditCardIcon
                          spacing="spacious"
                          label=""
                          color={token('color.icon.inverse')}
                        />
                      </DarkBg>
                    }
                    iconAfter={
                      <LinkExternalIcon
                        label=""
                        LEGACY_size="small"
                        size="small"
                      />
                    }
                    description={formatMessage(messages.billingDesc)}
                    href={`${adminHubUrl}/billing/o/${workspaceOrgId}/${ccpTransactionAccountId}/entitlement/${ccpEntitlementId}`}
                    target="_blank"
                  >
                    {formatMessage(messages.billingTitle)}
                  </LinkItem>
                )}
              </Section>
            )}
          {workspaceSettingsItem && (
            <Section title={formatMessage(messages.bitbucketAdministration)}>
              <LinkItem
                onClick={e =>
                  handleClick(e, 'workspace', workspaceSettingsItem.url)
                }
                iconBefore={
                  <BlueBg>
                    <ActivityIcon
                      label=""
                      size="small"
                      primaryColor={token('color.icon.inverse')}
                    />
                  </BlueBg>
                }
                description={formatMessage(messages.workspaceSettingsDesc)}
                href={workspaceSettingsItem.url}
              >
                {formatMessage(messages.workspaceSettingsTitle)}
              </LinkItem>
            </Section>
          )}
          <Section title={formatMessage(messages.personalSettings)}>
            {/* Atlassian settings menu item */}
            {manageAccountUrl && loginUrl && userEmail && (
              <LinkItem
                onClick={() => fireEvent('aid-account')}
                iconBefore={
                  <BlueBg>
                    <AtlassianIcon
                      label=""
                      size="small"
                      primaryColor={token('color.icon.inverse')}
                    />
                  </BlueBg>
                }
                iconAfter={
                  <LinkExternalIcon label="" LEGACY_size="small" size="small" />
                }
                description={formatMessage(messages.atlAcctSettingsDesc)}
                href={urls.external.atlassianAccountSync(
                  loginUrl,
                  manageAccountUrl,
                  userEmail
                )}
                target="_blank"
              >
                {formatMessage(messages.atlAcctSettingsTitle)}
              </LinkItem>
            )}

            {/* Bitbucket settings menu item */}
            <LinkItem
              onClick={e => handleClick(e, 'bbc-account', urls.ui.settings())}
              iconBefore={
                <BlueBg>
                  <PersonCircleIcon
                    label=""
                    size="small"
                    primaryColor={token('color.icon.inverse')}
                  />
                </BlueBg>
              }
              href={urls.ui.settings()}
              description={formatMessage(messages.bbAcctSettingsDesc)}
            >
              {formatMessage(messages.bbAcctSettingsTitle)}
            </LinkItem>

            {/* Labs menu item */}
            <LinkItem
              onClick={e => handleClick(e, 'labs', urls.ui.labs())}
              iconBefore={
                <BlueBg>
                  <LabsIcon
                    label=""
                    size="small"
                    primaryColor={token('color.icon.inverse')}
                  />
                </BlueBg>
              }
              href={urls.ui.labs()}
              description={formatMessage(messages.labsDesc)}
            >
              {formatMessage(messages.labsTitle)}
            </LinkItem>
          </Section>
        </PopupMenuGroup>
      );
    }, [
      loginUrl,
      manageAccountUrl,
      userEmail,
      formatMessage,
      workspaceSettingsItem,
      workspaceUuid,
      adminHubUrl,
      isOrgAdmin,
      workspaceOrgId,
      ccpEntitlementId,
      ccpTransactionAccountId,
      hasBillingAccess,
      fireEvent,
      handleClick,
    ]);

    const SettingsCog = useMemo(() => {
      return (tProps: any) => {
        const TriggerComponent = triggerComponent;
        return (
          <TriggerComponent
            {...tProps}
            tooltip={formatMessage(messages.settings)}
            onClick={onToggle}
            testId="h-nav-settings-cog"
            isSelected={isOpen}
          />
        );
      };
    }, [triggerComponent, onToggle, isOpen, formatMessage]);

    return (
      <Popup
        placement="bottom-end"
        content={SettingsMenu}
        isOpen={isOpen}
        onClose={onClose}
        trigger={SettingsCog}
      />
    );
  }
);

// Nav v3 wrapper component that provides workspace context via HorizontalNavContext
const HorizontalSettings = (): JSX.Element => {
  const {
    currentWorkspace,
    isOrgAdmin,
    workspaceOrgId,
    ccpEntitlementId,
    ccpTransactionAccountId,
    canViewEntitlement,
    manageAccountUrl,
    userEmail,
    adminHubUrl,
    loginUrl,
    menuItems,
    publishUiEvent,
  } = useContext<HorizontalNavProps>(HorizontalNavContext);

  return (
    <HorizontalSettingsBase
      currentWorkspace={currentWorkspace}
      isOrgAdmin={isOrgAdmin}
      workspaceOrgId={workspaceOrgId}
      ccpEntitlementId={ccpEntitlementId}
      ccpTransactionAccountId={ccpTransactionAccountId}
      canViewEntitlement={canViewEntitlement}
      manageAccountUrl={manageAccountUrl}
      userEmail={userEmail}
      adminHubUrl={adminHubUrl}
      loginUrl={loginUrl}
      menuItems={menuItems}
      publishUiEvent={publishUiEvent}
      triggerComponent={Settings}
    />
  );
};

export default HorizontalSettings;
